import {get, post} from "../../../../../assets/js/shared/data-manager";

const COLLECTION_STATE_CHANGED_EVENT = 'collection:state-changed';

class CollectionManagerClass {
    constructor() {
        this.collections = [];  // Holds the actual collections per type
        this.fetchPromises = []; // Acts as a lock to prevent concurrent fetches
    }

    /**
     * Get collections
     *
     * @return {Promise<*|null>}
     */
    async getCollections(publicationType) {
        // 1. If we already have the data, return it immediately
        if (this.collections[publicationType]) {
            return this.collections[publicationType];
        }

        // 2. Wait for a fetch specific to THIS type if one is running
        if (this.fetchPromises[publicationType]) {
            const result = await this.fetchPromises[publicationType];
            return result.collections;
        }

        // 3. Start a new fetch and save the promise lock FOR THIS TYPE
        this.fetchPromises[publicationType] = get(
            `${placeholders.router.baseUri}/ajax-data/account/collections`,
            {
                typeId: publicationType
            }
        );

        try {
            // Save the result to our mutable dictionary property
            const result = await this.fetchPromises[publicationType];
            this.collections[publicationType] = result.collections;
        } finally {
            // Clear the lock for this specific type
            this.fetchPromises[publicationType] = null;
        }

        return this.collections[publicationType];
    }/**
     * Check if a publication exists in ANY collection
     *
     * @param publicationType
     * @param publicationId
     * @return {Promise<boolean>}
     */
    async isPublicationInAnyCollection(publicationType, publicationId) {
        const collections = await this.getCollections(publicationType);
        let inCollection = false;

        collections.forEach(collection => {
            const InCurrentCollection = collection.publications.some(publication => publication.hashIdEncrypted === publicationId);
            if (InCurrentCollection) {
                inCollection = true;
            }
        });
        return inCollection;
    }

    /**
     * add a new collection
     *
     * @param collectionTitle
     * @param publicationType
     * @return {Promise<*>}
     */
    async addNewCollection(title, publicationType) {
        const result = await post(
                `${placeholders.router.baseUri}/ajax-data/account/collections/add`,
                {
                    title: title,
                    typeId: publicationType,
                }
        );

        // Only return the new collection
        return result.collection;
    }

    /**
     * Add a publication to a specific collection
     *
     * @param collectionId
     * @param publicationType
     * @param publicationId (The ID of the publication)
     * @return {Promise<null>}
     */
    async addPublicationToCollection(collectionId, publicationType, publicationId) {
        // Send the POST request to the abstract endpoint
        const result = await post(
                `${placeholders.router.baseUri}/ajax-data/account/collections/add/publication`,
                {
                    collectionId: collectionId,
                    publicationId: publicationId,
                    typeId: publicationType,
                }
        );

        this.collections[publicationType] = result.collections;
        this.dispatchStateChanged(publicationType, publicationId, true);

        return this.collections[publicationType];
    }

    /**
     * Remove a publication from a specific collection
     *
     * @param collectionId
     * @param publicationType
     * @param publicationId (The ID of the publication)
     * @return {Promise<null>}
     */
    async removePublicationFromCollection(collectionId, publicationType, publicationId) {
        // Send the POST request to the abstract endpoint
        const result = await post(
                `${placeholders.router.baseUri}/ajax-data/account/collections/remove/publication`,
                {
                    collectionId: collectionId,
                    publicationId: publicationId,
                    typeId: publicationType,
                }
        );

        this.collections[publicationType] = result.collections;
        this.dispatchStateChanged(publicationType, publicationId, false);
        return this.collections[publicationType];
    }

    /**
     * Remove collection
     * @param uuid
     * @return {Promise<*>}
     */
    async removeCollection(uuid) {
        // Send the POST request to the abstract endpoint
        const result = await post(
                `${placeholders.router.baseUri}/ajax-data/account/collections/remove`,
                {
                    uuid: uuid
                }
        );

        return result.success;
    }

    /**
     * Set Order indexes for the collections,
     * We do this by giving an array of uuid's in the prefered order
     *
     * @param sortedUuids
     * @return {Promise<*>}
     */
    async setOrderIndexes (sortedUuids) {
        const result = await post(
            `${placeholders.router.baseUri}/ajax-data/account/collections/set-sorting`,
            {
                sortedUuids: sortedUuids
            }
        )

        return result.success;
    }

    /**
     * Update collection,
     * We will set the title and update the order indexes for the linked publications
     * We do this by giving an array of publication hashIds in the prefered order
     *
     * @param uuid
     * @param title
     * @param sortedPublicationIds
     * @return {Promise<*>}
     */
    async updateCollection(uuid, title, sortedPublicationIds) {
        const result = await post(
                `${placeholders.router.baseUri}/ajax-data/account/collections/update`,
                {
                    uuid: uuid,
                    title: title,
                    sortedPublicationIds: sortedPublicationIds
                }
        )

        return result.success;
    }

    /**
     * Dispatch a global event so every like badge can sync itself.
     *
     * @param {String} publicationType
     * @param {String} publicationId
     * @param {boolean} state
     */
    dispatchStateChanged(publicationType, publicationId, state) {
        document.dispatchEvent(new CustomEvent(COLLECTION_STATE_CHANGED_EVENT, {
            detail: {
                publicationType: String(publicationType),
                publicationId: String(publicationId),
                state: Boolean(state)
            }
        }));
    }
}

const CollectionManager = new CollectionManagerClass();

export default CollectionManager;